import React, {useMemo} from "react";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";

import { blue, red, lightBlue, lime } from '@mui/material/colors'

import {
    getSeason,
    extractAvailableSeasons,
    getDayOfYearLabel,
    groupBy,
    extractSeason,
    createDayOfMonthLabels,
    trimLabels,
    isLeapYear,
} from "../utils";

import {useToolbar} from "./Toolbar";
import Toolbar from "./Toolbar";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";


const TempGraph = ({data: originalData, station}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const data = useMemo(() => (originalData.map(({timestamp, ...rest}) => {
            return {
                timestamp,
                label: getDayOfYearLabel(timestamp),
                season: getSeason(timestamp),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbarState = useToolbar(availableSeasons);
    const selectedSeason = toolbarState.selectedSeason;
    const selectedSeasonData = groupBy(extractSeason(data, selectedSeason), 'label')

    const withLeapYears = availableSeasons.some(y => isLeapYear(y) + 1)
    const labels = createDayOfMonthLabels(withLeapYears);

    const trimmedLabels = trimLabels(labels, selectedSeasonData);

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `${station?.code ?? 'station'}_temp`
        },
        title: {
            text: `${station?.code ?? ''} - ${station?.name ?? ''} (${station?.elevation ?? '----'} m)`,
        },
        subtitle: {
            text: t('tempGraph.title'),
        },
        series: [
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.tMin ?? null),
                type: 'spline',
                name: t('tempGraph.legend.tMin'),
                color: blue[900],
                yAxis: 0,
            },
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.tMax ?? null),
                type: 'spline',
                name: t('tempGraph.legend.tMax'),
                color: red[500],
                yAxis: 0,
            },
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.ta ?? null),
                type: 'spline',
                name: t('tempGraph.legend.ta'),
                color: lightBlue[500],
                yAxis: 0,
                visible: false
            },
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.th10 ?? null),
                type: 'spline',
                name: t('tempGraph.legend.th10'),
                color: lime[400],
                yAxis: 0,
                visible: false
            },
            {
                data: trimmedLabels.map(l => selectedSeasonData[l]?.[0]?.th30 ?? null),
                type: 'spline',
                name: t('tempGraph.legend.th30'),
                color: lime[900],
                yAxis: 0,
                visible: false
            },
        ],
        xAxis: {
            categories: trimmedLabels
        },
        yAxis: [
            {
                title: {
                    text: 'TA [°C]'
                }
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    radius: 2,
                    symbol: 'circle',
                },
                spline: {
                    connectNulls: false, // Do not connect null points
                },
                states: {
                    hover: {
                        enabled: false
                    }
                }
            },
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    return <>
        <Toolbar {...toolbarState} availableSeasons={availableSeasons} />
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
        />
    </>

}

export default TempGraph;